<template>
  <div class="cc">
    <!-- Anexos escolhidos, antes de enviar -->
    <transition name="cc-stage">
      <div
        v-if="staged.length"
        class="cc__stage"
      >
        <div
          v-for="(item, index) in staged"
          :key="item.id"
          class="cc__chip"
        >
          <img
            v-if="item.preview"
            :src="item.preview"
            :alt="item.file.name"
          >
          <span
            v-else
            class="cc__chip-icon"
          ><feather-icon
            icon="FileIcon"
            size="20"
          /></span>
          <span class="cc__chip-name">
            <strong>{{ item.file.name }}</strong>
            <small>{{ size(item.file.size) }}</small>
          </span>
          <button
            type="button"
            class="cc__chip-remove"
            :aria-label="$t('chat.removeAttachment')"
            @click="unstage(index)"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>
      </div>
    </transition>

    <!-- Respondendo ou editando uma mensagem -->
    <transition name="cc-stage">
      <div
        v-if="context"
        class="cc__context"
      >
        <feather-icon
          :icon="editing ? 'Edit2Icon' : 'CornerUpLeftIcon'"
          size="18"
          class="cc__context-icon"
        />
        <span class="cc__context-text">
          <strong>{{ editing ? $t('chat.editingMessage') : $t('chat.replyingTo', { name: replyName }) }}</strong>
          <small>{{ contextPreview }}</small>
        </span>
        <button
          type="button"
          class="cc__chip-remove"
          :aria-label="$t('chat.cancel')"
          @click="$emit('cancel-context')"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>
    </transition>

    <!-- Gravando áudio -->
    <div
      v-if="recording"
      class="cc__bar cc__bar--recording"
    >
      <button
        type="button"
        class="cc__icon-btn cc__icon-btn--danger"
        :title="$t('chat.cancelRecording')"
        @click="cancelRecording"
      >
        <feather-icon
          icon="TrashIcon"
          size="20"
        />
      </button>
      <span class="cc__rec-dot" />
      <span class="cc__rec-time">{{ clock(recordSeconds) }}</span>
      <div
        class="cc__wave"
        :class="{ 'cc__wave--silent': noVoice }"
        aria-hidden="true"
      >
        <i
          v-for="(level, i) in levels"
          :key="i"
          :style="{ height: `${Math.max(12, level * 100)}%` }"
        />
      </div>
      <span class="cc__rec-hint">{{ noVoice ? $t('chat.noVoice') : $t('chat.recording') }}</span>
      <button
        type="button"
        class="cc__send"
        :title="$t('chat.sendAudio')"
        @click="finishRecording"
      >
        <feather-icon
          icon="SendIcon"
          size="20"
        />
      </button>
    </div>

    <div
      v-else
      class="cc__bar"
    >
      <div
        ref="menuAnchor"
        class="cc__attach"
      >
        <button
          type="button"
          class="cc__icon-btn"
          :title="$t('chat.attach')"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          @click.stop="menuOpen = !menuOpen"
        >
          <feather-icon
            icon="PaperclipIcon"
            size="21"
          />
        </button>
        <transition name="cc-menu">
          <div
            v-if="menuOpen"
            class="cc__menu"
          >
            <button
              type="button"
              @click="pick('image/*')"
            >
              <span class="cc__menu-icon cc__menu-icon--image"><feather-icon
                icon="ImageIcon"
                size="18"
              /></span>
              {{ $t('chat.attachImage') }}
            </button>
            <button
              type="button"
              @click="pick('')"
            >
              <span class="cc__menu-icon cc__menu-icon--file"><feather-icon
                icon="FileTextIcon"
                size="18"
              /></span>
              {{ $t('chat.attachFile') }}
            </button>
          </div>
        </transition>
        <input
          ref="fileInput"
          type="file"
          multiple
          hidden
          @change="onPicked"
        >
      </div>

      <textarea
        ref="input"
        v-model="text"
        class="cc__input"
        rows="1"
        :placeholder="$t('chat.typeMessage')"
        :aria-label="$t('chat.typeMessage')"
        maxlength="4000"
        @input="autosize"
        @keydown.enter.exact.prevent="submit"
        @paste="onPaste"
      />

      <button
        v-if="canSend"
        type="button"
        class="cc__send"
        :title="$t('chat.send')"
        @click="submit"
      >
        <feather-icon
          icon="SendIcon"
          size="20"
        />
      </button>
      <button
        v-else
        type="button"
        class="cc__icon-btn"
        :title="$t('chat.recordAudio')"
        :disabled="!canRecord || !!editing"
        @click="startRecording"
      >
        <feather-icon
          icon="MicIcon"
          size="21"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { formatBytes, formatDuration, messagePreviewKey } from '@/utils/chat'

const IMAGE_MAX = 10 * 1024 * 1024
const FILE_MAX = 20 * 1024 * 1024
const AUDIO_MAX_SECONDS = 300
const BLOCKED = ['exe', 'msi', 'bat', 'cmd', 'com', 'scr', 'pif', 'cpl', 'jar', 'js', 'mjs', 'vbs', 'vbe', 'wsf', 'ps1', 'sh', 'php', 'phtml', 'phar', 'html', 'htm', 'svg', 'hta', 'lnk', 'dll', 'apk', 'dmg']
const MAX_STAGED = 10
// Ordem de preferência: o que o navegador souber gravar e que os outros navegadores e o app saibam tocar.
// Extensão pelo formato gravado ('webm' é o último e serve de padrão).
const AUDIO_EXTENSIONS = [['mp4', 'm4a'], ['ogg', 'ogg'], ['', 'webm']]
const WAVE_BARS = 36
const LEVEL_INTERVAL_MS = 70
// Volume (RMS, 0–1) a partir do qual conta como voz, e por quanto tempo de silêncio vem o aviso.
const VOICE_THRESHOLD = 0.035
const SILENCE_WARN_MS = 3000
const RECORDER_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/webm']

export default {
  name: 'ChatComposer',

  props: {
    // Mensagem que está sendo respondida / editada (uma só, ou nenhuma).
    reply: { type: Object, default: null },
    editing: { type: Object, default: null },
    replyName: { type: String, default: '' },
  },

  data() {
    return {
      text: '',
      staged: [],
      menuOpen: false,
      recording: false,
      recordSeconds: 0,
      recorder: null,
      recordTimer: null,
      recordChunks: [],
      recordCanceled: false,
      // Detecção de voz: níveis recentes do microfone (0–1) para a onda e aviso de silêncio.
      levels: new Array(WAVE_BARS).fill(0),
      noVoice: false,
    }
  },

  computed: {
    canSend() {
      return this.text.trim().length > 0 || (!this.editing && this.staged.length > 0)
    },
    context() {
      return this.editing || this.reply
    },
    contextPreview() {
      const message = this.context
      if (!message) return ''
      const key = messagePreviewKey(message)

      return message.body || (key ? this.$t(key) : '')
    },
    canRecord() {
      return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder)
    },
  },

  watch: {
    // Editar traz o texto da mensagem para o campo; sair da edição devolve o campo vazio.
    editing(message, previous) {
      if (message) {
        this.text = message.body || ''
        this.$nextTick(() => { this.autosize(); this.focus() })
      } else if (previous) {
        this.text = ''
        this.$nextTick(this.autosize)
      }
    },
    reply(message) {
      if (message) this.focus()
    },
  },

  mounted() {
    document.addEventListener('click', this.closeMenu)
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu)
    this.staged.forEach(this.revoke)
    this.teardownRecorder()
  },

  methods: {
    size: formatBytes,
    clock: formatDuration,

    focus() {
      if (this.$refs.input) this.$refs.input.focus()
    },

    closeMenu(event) {
      if (this.menuOpen && this.$refs.menuAnchor && !this.$refs.menuAnchor.contains(event.target)) this.menuOpen = false
    },

    pick(accept) {
      this.menuOpen = false
      this.$refs.fileInput.accept = accept
      this.$refs.fileInput.click()
    },

    onPicked(event) {
      const input = event.target
      this.stage(Array.from(input.files))
      input.value = ''
    },

    // Print da tela colado direto no campo vira anexo (muito usado no dia a dia da central).
    onPaste(event) {
      const files = Array.from(event.clipboardData ? event.clipboardData.files : [])
      if (!files.length) return
      event.preventDefault()
      this.stage(files)
    },

    /** Também chamado pela tela de conversa quando o usuário solta arquivos nela. */
    stage(files) {
      files.forEach(file => {
        if (this.staged.length >= MAX_STAGED) return
        const error = this.validate(file)
        if (error) {
          this.$message(null, error, 'error')
          return
        }
        this.staged.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${this.staged.length}`,
          file,
          preview: file.type.startsWith('image/') && !file.type.includes('svg') ? URL.createObjectURL(file) : null,
        })
      })
      this.focus()
    },

    validate(file) {
      const extension = (file.name.split('.').pop() || '').toLowerCase()
      if (BLOCKED.includes(extension)) return this.$t('chat.errors.blocked', { name: file.name })
      const limit = file.type.startsWith('image/') && !file.type.includes('svg') ? IMAGE_MAX : FILE_MAX
      if (file.size > limit) return this.$t('chat.errors.tooLarge', { name: file.name, max: formatBytes(limit) })
      if (file.size === 0) return this.$t('chat.errors.empty', { name: file.name })

      return null
    },

    unstage(index) {
      this.revoke(this.staged[index])
      this.staged.splice(index, 1)
    },

    revoke(item) {
      if (item.preview) URL.revokeObjectURL(item.preview)
    },

    submit() {
      if (!this.canSend) return
      const body = this.text.trim()

      if (this.editing) {
        this.$emit('save-edit', body)
        return
      }

      if (this.staged.length) {
        // A legenda vai junto do primeiro anexo; os demais seguem sem texto.
        this.staged.forEach((item, index) => {
          this.$emit('send-file', { file: item.file, body: index === 0 ? body : '' })
          this.revoke(item)
        })
        this.staged = []
      } else {
        this.$emit('send-text', body)
      }

      this.text = ''
      this.$nextTick(this.autosize)
    },

    autosize() {
      const el = this.$refs.input
      if (!el) return
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`
    },

    // ── gravação de áudio ────────────────────────────────────────────────

    async startRecording() {
      if (!this.canRecord || this.recording) return
      let stream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (e) {
        this.$message(null, this.$t('chat.errors.micDenied'), 'error')
        return
      }

      const mimeType = RECORDER_TYPES.find(t => window.MediaRecorder.isTypeSupported(t)) || ''
      this.recordChunks = []
      this.recordCanceled = false
      this.recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      this.recorder.ondataavailable = e => { if (e.data.size) this.recordChunks.push(e.data) }
      this.recorder.onstop = this.onRecorderStop
      this.recorder.start()
      this.startMeter(stream)

      this.recording = true
      this.recordSeconds = 0
      this.recordTimer = setInterval(() => {
        this.recordSeconds += 1
        if (this.recordSeconds >= AUDIO_MAX_SECONDS) this.finishRecording()
      }, 1000)
    },

    finishRecording() {
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop()
    },

    cancelRecording() {
      this.recordCanceled = true
      this.finishRecording()
    },

    onRecorderStop() {
      const seconds = this.recordSeconds
      const type = (this.recorder && this.recorder.mimeType) || 'audio/webm'
      const chunks = this.recordChunks
      const canceled = this.recordCanceled
      this.teardownRecorder()

      if (canceled || !chunks.length || seconds < 1) return
      const extension = AUDIO_EXTENSIONS.find(([part]) => type.includes(part))[1]
      const file = new File(chunks, `audio.${extension}`, { type: type.split(';')[0] })
      this.$emit('send-voice', { file, duration: seconds })
    },

    /** Mede o volume do microfone ao vivo: alimenta a onda e avisa quando não há voz. */
    startMeter(stream) {
      const Context = window.AudioContext || window.webkitAudioContext
      if (!Context) return
      this.audioContext = new Context()
      const analyser = this.audioContext.createAnalyser()
      analyser.fftSize = 512
      this.audioContext.createMediaStreamSource(stream).connect(analyser)
      const samples = new Uint8Array(analyser.fftSize)
      let silentSince = Date.now()
      this.levels = new Array(WAVE_BARS).fill(0)
      this.noVoice = false

      this.meterTimer = setInterval(() => {
        analyser.getByteTimeDomainData(samples)
        let sum = 0
        for (let i = 0; i < samples.length; i += 1) {
          const v = (samples[i] - 128) / 128
          sum += v * v
        }
        const rms = Math.sqrt(sum / samples.length)
        // Curva de raiz: fala normal (RMS baixo) já enche bem a onda.
        this.levels = this.levels.slice(1).concat(Math.min(1, Math.sqrt(rms) * 1.6))

        if (rms >= VOICE_THRESHOLD) silentSince = Date.now()
        this.noVoice = Date.now() - silentSince > SILENCE_WARN_MS
      }, LEVEL_INTERVAL_MS)
    },

    stopMeter() {
      clearInterval(this.meterTimer)
      this.meterTimer = null
      if (this.audioContext) this.audioContext.close().catch(() => {})
      this.audioContext = null
      this.noVoice = false
    },

    teardownRecorder() {
      this.stopMeter()
      clearInterval(this.recordTimer)
      this.recordTimer = null
      if (this.recorder) {
        this.recorder.ondataavailable = null
        this.recorder.onstop = null
        // Solta o microfone (a bolinha de "gravando" do navegador some).
        if (this.recorder.stream) this.recorder.stream.getTracks().forEach(t => t.stop())
        if (this.recorder.state !== 'inactive') this.recorder.stop()
      }
      this.recorder = null
      this.recording = false
    },
  },
}
</script>

<style scoped>
.cc {
  position: relative;
  border-top: 1px solid var(--chat-border);
  background: var(--chat-header);
}

.cc__stage {
  display: flex;
  gap: 8px;
  padding: 10px 14px 0;
  overflow-x: auto;
}

.cc__chip {
  position: relative;
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
  max-width: 240px;
  padding: 6px 28px 6px 6px;
  border: 1px solid var(--chat-border);
  border-radius: 10px;
  background: var(--chat-surface);
}

.cc__chip img { width: 44px; height: 44px; border-radius: 6px; object-fit: cover; }

.cc__chip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  background: rgba(115, 103, 240, 0.15);
  color: #7367f0;
}

.cc__chip-name { display: flex; flex-direction: column; min-width: 0; font-size: 0.8rem; }
.cc__chip-name strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cc__chip-name small { opacity: 0.65; }

.cc__chip-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  padding: 2px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--chat-muted);
}

.cc__chip-remove:hover { background: rgba(127, 127, 127, 0.2); }

.cc__context {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 14px 0;
  padding: 7px 34px 7px 10px;
  border-left: 3px solid #7367f0;
  border-radius: 8px;
  background: rgba(115, 103, 240, 0.1);
}

.cc__context-icon { flex: none; color: #7367f0; }
.cc__context-text { display: flex; flex-direction: column; min-width: 0; line-height: 1.3; }
.cc__context-text strong { color: #7367f0; font-size: 0.8rem; }

.cc__context-text small {
  overflow: hidden;
  color: var(--chat-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc__bar {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 10px 14px;
}

.cc__bar--recording { align-items: center; gap: 12px; }

.cc__input {
  flex: 1;
  min-height: 42px;
  max-height: 140px;
  padding: 10px 14px;
  border: 1px solid transparent;
  border-radius: 22px;
  outline: 0;
  background: var(--chat-input);
  color: var(--chat-text);
  font-size: 0.94rem;
  line-height: 1.35;
  resize: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cc__input:focus {
  border-color: rgba(115, 103, 240, 0.55);
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}

.cc__icon-btn,
.cc__send {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  transition: background 0.15s ease, transform 0.1s ease, color 0.15s ease;
}

.cc__icon-btn { background: transparent; color: var(--chat-muted); }
.cc__icon-btn:hover:not(:disabled) { background: rgba(115, 103, 240, 0.12); color: #7367f0; }
.cc__icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.cc__icon-btn--danger:hover { background: rgba(234, 84, 85, 0.14) !important; color: #ea5455 !important; }

.cc__send { background: #7367f0; color: #fff; box-shadow: 0 2px 6px rgba(115, 103, 240, 0.4); }
.cc__send:hover { background: #5e50ee; }
.cc__send:active { transform: scale(0.94); }

.cc__rec-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ea5455;
  animation: cc-pulse 1.1s ease-in-out infinite;
}

.cc__rec-time { font-variant-numeric: tabular-nums; font-weight: 600; }
.cc__rec-hint { min-width: 0; color: var(--chat-muted); font-size: 0.85rem; transition: color 0.2s ease; }

/* Onda do microfone: cada barra é um instante recente; muda de cor quando não há voz. */
.cc__wave {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 2px;
  height: 30px;
  min-width: 60px;
}

.cc__wave i {
  flex: 1;
  max-width: 4px;
  border-radius: 2px;
  background: #7367f0;
  transition: height 0.07s linear, background 0.2s ease;
}

.cc__wave--silent i { background: #ff9f43; }
.cc__wave--silent + .cc__rec-hint { color: #ff9f43; }

@keyframes cc-pulse { 50% { opacity: 0.25; } }

.cc__attach { position: relative; }

.cc__menu {
  position: absolute;
  bottom: 52px;
  left: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  min-width: 190px;
  padding: 6px;
  border-radius: 12px;
  background: var(--chat-surface);
  box-shadow: 0 8px 28px rgba(34, 41, 47, 0.25);
  transform-origin: bottom left;
}

.cc__menu button {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 10px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--chat-text);
  text-align: left;
}

.cc__menu button:hover { background: rgba(127, 127, 127, 0.12); }

.cc__menu-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #fff;
}

.cc__menu-icon--image { background: #a855f7; }
.cc__menu-icon--file { background: #5b6cf0; }

.cc-menu-enter-active, .cc-menu-leave-active { transition: opacity 0.14s ease, transform 0.14s ease; }
.cc-menu-enter, .cc-menu-leave-to { opacity: 0; transform: translateY(6px) scale(0.96); }
.cc-stage-enter-active, .cc-stage-leave-active { transition: opacity 0.15s ease; }
.cc-stage-enter, .cc-stage-leave-to { opacity: 0; }

@media (max-width: 767px) {
  .cc__bar { gap: 4px; padding: 8px 8px calc(8px + env(safe-area-inset-bottom, 0px)); }
  /* 16px: abaixo disso o iOS dá zoom na página ao focar o campo. */
  .cc__input { font-size: 16px; }
  .cc__bar--recording { gap: 8px; }
  .cc__rec-hint { font-size: 0.72rem; line-height: 1.15; }
}
</style>
